import { EmailAuthProvider, getIdTokenResult, linkWithPopup, reauthenticateWithCredential, reauthenticateWithPopup, verifyBeforeUpdateEmail } from 'firebase/auth';
import { googleProvider } from './firebase';
import { deliverVerification } from './auth-email';

export const GRACE_CLAIM = 'tradelog_legacy_until';
export function accessFromClaims(claims = {}, now = Date.now()) {
  const until = claims[GRACE_CLAIM];
  const verified = claims.email_verified === true;
  const graceUntil = Number.isSafeInteger(until) && until > now ? until : 0;
  return {allowed:verified || graceUntil > 0, verified, graceUntil:verified ? 0 : graceUntil};
}
export async function readAccess(user) {
  const token = await getIdTokenResult(user, true);
  return accessFromClaims(token.claims);
}
export const passwordAccount = user => user?.providerData?.some(p=>p.providerId==='password');
export const googleAccount = user => user?.providerData?.some(p=>p.providerId==='google.com');
export function authError(error) {
  const messages = {
    'auth/invalid-credential':'Incorrect email or password.',
    'auth/wrong-password':'Incorrect email or password.',
    'auth/user-not-found':'Incorrect email or password.',
    'auth/too-many-requests':'Too many attempts. Please wait before trying again.',
    'auth/network-request-failed':'Check your connection and try again.',
    'auth/email-delivery-unavailable':'The email service is temporarily unavailable. Please try again shortly.',
    'auth/requires-recent-login':'Sign in again to confirm this change.',
    'auth/credential-already-in-use':'That Google account is linked to another TradeLog account. Sign in to that account separately; your journals have not been merged.',
    'auth/email-already-in-use':'This email cannot be used here. Try signing in or resetting its password.',
    'auth/account-exists-with-different-credential':'Sign in using your existing method, then connect Google from Settings → Account.',
    'auth/popup-closed-by-user':'Google sign-in was cancelled. Your account is unchanged.',
    'auth/popup-blocked':'Allow the Google sign-in popup, then try again.',
    'auth/unauthorized-domain':'Google sign-in is not available from this address. Open www.trade-log.co.uk and try again.',
    'auth/cancelled-popup-request':'Another sign-in window was opened. Continue in the newest window.',
    'auth/invalid-email':'Enter a valid email address.',
    'auth/weak-password':'Choose a longer password that meets the password requirements.',
    'auth/admin-restricted-operation':'New registrations are closed. Request paid access through TradeLog Support, or sign in with your existing account.',
    'auth/operation-not-allowed':'This sign-in method is unavailable. Please contact support.',
  };
  return messages[error?.code] || 'The request could not be completed. Please try again.';
}
export async function confirmIdentity(user, password) {
  if (passwordAccount(user)) {
    if (!password) throw Object.assign(new Error('Password required'),{code:'auth/requires-recent-login'});
    await reauthenticateWithCredential(user,EmailAuthProvider.credential(user.email,password));
  } else if (googleAccount(user)) await reauthenticateWithPopup(user,googleProvider);
  else throw Object.assign(new Error('Sign in again'),{code:'auth/requires-recent-login'});
}
export async function requestEmailChange(user, email, password) {
  await confirmIdentity(user,password);
  await verifyBeforeUpdateEmail(user,email.trim());
}
export async function connectGoogle(user, password) {
  await confirmIdentity(user,password);
  const result = await linkWithPopup(user,googleProvider);
  if (result.user.uid !== user.uid) throw new Error('Unexpected account change');
  await user.getIdToken(true);
  return result;
}

// A local cooldown is for feedback only; Firebase enforces sending limits remotely.
const lastSent = new Map();
const sending = new Set();
export function verificationWait(uid, now=Date.now()) {return Math.max(0,Math.ceil(((lastSent.get(uid)||0)+60000-now)/1000));}
export async function sendVerification(user) {
  if(sending.has(user.uid)||verificationWait(user.uid)>0)return false;
  sending.add(user.uid);
  try {await deliverVerification(user);lastSent.set(user.uid,Date.now());return true;}
  finally {sending.delete(user.uid);}
}
